import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonVariant,
  Show
} from "shared/components";
import { Checkbox, CheckboxSize } from "shared/components/forms/Checkbox";
import { Loader } from "shared/components/Loader";
import { Modal } from "shared/components/Modal";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useAuthContext } from "providers/Auth";
import { toMoney, cx, getDebtTypeLabel } from "shared/utils";
import { getApplicantRole } from "utils/getApplicantRole";
import { ApplicantType } from "shared/types/application";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import type { GroupDebtDetails, DebtItem } from "../types";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

const EDIT_DENIED_TOOLTIP = "You don't have permissions to edit EMP";

// Wraps a debt toggle so a user without the edit permission gets an explanation on hover
// instead of a silently inert checkbox.
const EditPermissionTooltip = ({
  isDenied,
  className,
  children
}: {
  isDenied: boolean;
  className?: string;
  children: ReactNode;
}) => {
  if (!isDenied) return children;

  return (
    <Tooltip placement="bottom-start" delay={{ open: 300 }}>
      <TooltipTrigger asChild>
        <span className={className}>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] rounded-lg border border-alpha-40 bg-alpha-800 px-3 py-2 text-sm text-white z-[1000]">
        {EDIT_DENIED_TOOLTIP}
      </TooltipContent>
    </Tooltip>
  );
};

const getIncludedDebtMap = (debtDetails: GroupDebtDetails) =>
  debtDetails.applicant_debts.reduce<Record<string, boolean>>((acc, group) => {
    group.debts.forEach((debt) => {
      acc[debt.id] = debt.included;
    });
    return acc;
  }, {});

const StatItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center gap-2">
    <span className="text-content-400 font-medium text-sm">{label}</span>
    <span className="text-content-1000 font-bold text-base">{value}</span>
  </div>
);

const SeparatorDot = () => (
  <span className="size-2 shrink-0 rounded-full bg-alpha-80" />
);

const RentLineItem = ({
  label,
  amount,
  isNegative
}: {
  label: string;
  amount: number;
  isNegative?: boolean;
}) => (
  <div className="flex justify-between items-center text-sm font-medium">
    <span className="text-content-700">{label}</span>
    <span className={isNegative ? "text-danger-600" : "text-content-700"}>
      {isNegative ? "-" : ""}
      {toMoney(Math.abs(amount) / 100)}/mo
    </span>
  </div>
);

const DebtLineItem = ({
  debt,
  checked,
  onToggle,
  isEditDenied
}: {
  debt: DebtItem;
  checked: boolean;
  onToggle: () => void;
  isEditDenied: boolean;
}) => (
  <div className="flex items-center justify-between">
    <EditPermissionTooltip isDenied={isEditDenied} className="min-w-0 flex-1">
      <Checkbox
        size={CheckboxSize.BASE}
        checked={checked}
        disabled={isEditDenied}
        onChange={onToggle}
        label={
          <div>
            <p className="text-sm text-content-700 font-medium">
              {getDebtTypeLabel(debt.description)}
            </p>
            <p className="text-sm text-content-300 font-medium">{debt.name}</p>
          </div>
        }
      />
    </EditPermissionTooltip>
    <div className="flex items-center gap-4">
      <Show if={debt.is_potential_duplicate}>
        <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          Potential duplicate
        </Badge>
      </Show>
      <span
        className={
          checked
            ? "text-sm text-content-1000 font-medium"
            : "text-sm text-content-100 font-medium line-through"
        }
      >
        {toMoney(debt.amount.cents / 100)}/mo
      </span>
    </div>
  </div>
);

export const EditEstimatedMonthlyPaymentsModal = ({
  opened,
  onClose,
  applicationId
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
}) => {
  const queryClient = useQueryClient();
  const { permissions } = useAuthContext();
  const isEditDenied = !permissions.canEditEstimatedMonthlyPayments;

  const {
    data: debtDetails,
    isLoading,
    isError
  } = useQuery<GroupDebtDetails>({
    queryKey: ["GROUP_DEBT_DETAILS", applicationId],
    queryFn: () =>
      instance
        .get(`/portal/v1/applications/${applicationId}/group_debt_details`)
        .then(({ data }) => data)
  });

  const [includedDebts, setIncludedDebts] = useState<Record<string, boolean>>(
    {}
  );

  const initialIncludedDebts = useMemo(
    () => (debtDetails ? getIncludedDebtMap(debtDetails) : {}),
    [debtDetails]
  );

  useEffect(() => {
    if (debtDetails) setIncludedDebts(getIncludedDebtMap(debtDetails));
  }, [debtDetails]);

  const hasChanges = useMemo(
    () =>
      Object.keys(initialIncludedDebts).some(
        (id) => includedDebts[id] !== initialIncludedDebts[id]
      ),
    [includedDebts, initialIncludedDebts]
  );

  const toggleDebt = (debtId: string) => {
    setIncludedDebts((prev) => ({ ...prev, [debtId]: !prev[debtId] }));
  };

  const toggleApplicantDebts = (debts: DebtItem[]) => {
    const allIncluded = debts.every((debt) => includedDebts[debt.id]);
    setIncludedDebts((prev) => {
      const next = { ...prev };
      debts.forEach((debt) => {
        next[debt.id] = !allIncluded;
      });
      return next;
    });
  };

  const resetToDefaults = useCallback(() => {
    if (!debtDetails) return;
    const allIncluded = debtDetails.applicant_debts.reduce<
      Record<string, boolean>
    >((acc, group) => {
      group.debts.forEach((debt) => {
        acc[debt.id] = !debt.is_potential_duplicate;
      });
      return acc;
    }, {});
    setIncludedDebts(allIncluded);
  }, [debtDetails]);

  const calculatedValues = useMemo(() => {
    if (!debtDetails)
      return {
        totalEmpCents: 0,
        debtToNetIncome: 0,
        debtToGrossIncome: 0,
        perApplicantTotals: []
      };

    const rentCents = debtDetails.application_rent.total.cents;

    const debtCents = debtDetails.applicant_debts.reduce((total, group) => {
      return (
        total +
        group.debts.reduce((groupTotal, debt) => {
          if (includedDebts[debt.id]) return groupTotal + debt.amount.cents;
          return groupTotal;
        }, 0)
      );
    }, 0);

    const totalEmpCents = rentCents + debtCents;
    const { net_income: netIncome, gross_income: grossIncome } = debtDetails;

    let debtToNetIncome = debtDetails.debt_to_net_income * 100;
    if (hasChanges && netIncome > 0)
      debtToNetIncome = (totalEmpCents / 100 / netIncome) * 100;

    let debtToGrossIncome = debtDetails.debt_to_gross_income * 100;
    if (hasChanges && grossIncome > 0)
      debtToGrossIncome = (totalEmpCents / 100 / grossIncome) * 100;

    const perApplicantTotals = debtDetails.applicant_debts.map((group) => ({
      applicant_id: group.applicant_id,
      total: group.debts.reduce((sum, debt) => {
        if (includedDebts[debt.id]) return sum + debt.amount.cents;
        return sum;
      }, 0)
    }));

    return {
      totalEmpCents,
      debtToNetIncome,
      debtToGrossIncome,
      perApplicantTotals
    };
  }, [includedDebts, debtDetails, hasChanges]);

  const { mutate: saveChanges, isPending } = useMutation({
    mutationKey: ["UPDATE_GROUP_DEBT_INCLUSIONS", applicationId],
    mutationFn: () => {
      const excludedDebtIds = Object.entries(includedDebts)
        .filter(([, included]) => !included)
        .map(([id]) => id);

      return instance
        .patch(
          `/portal/v1/applications/${applicationId}/group_debt_overrides`,
          {
            excluded_debt_ids: excludedDebtIds
          }
        )
        .then(({ data }) => data as GroupDebtDetails);
    },
    onSuccess: (updatedDetails) => {
      queryClient.setQueryData(
        ["GROUP_DEBT_DETAILS", applicationId],
        updatedDetails
      );
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      toast.success("Estimated monthly payments updated");
      onClose();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err.message));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[800px] w-full max-h-[90vh] overflow-hidden flex flex-col"
    >
      <header className="px-6 py-4 border-b border-alpha-40 flex justify-between">
        <div>
          <div className="flex justify-between items-center">
            <h4 className="text-xl font-bold text-content-1000">
              Edit estimated monthly payments
            </h4>
          </div>
          <p className="text-sm text-content-400 mt-2 font-normal">
            Toggle which debts to include in the debt to net income calculation
          </p>
        </div>
        <IconButton
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
          onClick={onClose}
          className="mb-6"
        />
      </header>

      <Show if={isLoading}>
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      </Show>

      <Show if={!isLoading && (isError || !debtDetails)}>
        <div className="flex items-center justify-center py-12 text-sm text-content-400">
          Failed to load debt details
        </div>
      </Show>

      <Show if={!isLoading && !isError && debtDetails != null}>
        <div className="px-6 py-4 border-b border-alpha-40 flex items-center gap-4 flex-wrap shrink-0">
          <StatItem
            label="EMP"
            value={toMoney(calculatedValues.totalEmpCents / 100) ?? "-"}
          />
          <SeparatorDot />
          <StatItem
            label="Net income"
            value={toMoney(debtDetails?.net_income ?? 0) ?? "-"}
          />
          <SeparatorDot />
          <StatItem
            label="Debt to net income"
            value={`${calculatedValues.debtToNetIncome.toFixed(2)}%`}
          />
          <SeparatorDot />
          <StatItem
            label="Debt to gross income"
            value={`${calculatedValues.debtToGrossIncome.toFixed(2)}%`}
          />
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4 bg-background-900">
          <div className="rounded-lg border border-alpha-40 bg-background-1000 shadow-xs">
            <div className="flex justify-between items-center px-6 py-4 border-b border-alpha-40">
              <span className="text-base font-medium text-content-700">
                Application rent
              </span>
              <h5 className="text-base font-bold text-content-1000">
                {toMoney(
                  (debtDetails?.application_rent.total.cents ?? 0) / 100
                )}
                /mo
              </h5>
            </div>
            <div className="space-y-2 px-6 py-4">
              <RentLineItem
                label="Listed rent"
                amount={debtDetails?.application_rent.listed_rent.cents ?? 0}
              />
              <Show
                if={(debtDetails?.application_rent.pet_rent.cents ?? 0) > 0}
              >
                <RentLineItem
                  label="Pet rent"
                  amount={debtDetails?.application_rent.pet_rent.cents ?? 0}
                />
              </Show>
              <Show
                if={
                  (debtDetails?.application_rent.housing_assistance.cents ??
                    0) !== 0
                }
              >
                <RentLineItem
                  label="Housing assistance"
                  amount={
                    debtDetails?.application_rent.housing_assistance.cents ?? 0
                  }
                  isNegative
                />
              </Show>
            </div>
          </div>

          {debtDetails?.applicant_debts.map((group) => {
            const applicantTotal = calculatedValues.perApplicantTotals.find(
              (t) => t.applicant_id === group.applicant_id
            );

            return (
              <div
                key={group.applicant_id}
                className="rounded-lg border border-alpha-40 bg-background-1000 shadow-xs"
              >
                <div
                  className={cx(
                    "flex items-center gap-3 px-6 py-4",
                    group.debts.length > 0 && "border-b border-alpha-40"
                  )}
                >
                  <Show if={group.debts.length > 0}>
                    <EditPermissionTooltip
                      isDenied={isEditDenied}
                      className="inline-flex shrink-0"
                    >
                      <Checkbox
                        size={CheckboxSize.BASE}
                        checked={group.debts.some(
                          (debt) => includedDebts[debt.id]
                        )}
                        disabled={isEditDenied}
                        onChange={() => toggleApplicantDebts(group.debts)}
                        wrapperClassName="!w-auto shrink-0"
                      />
                    </EditPermissionTooltip>
                  </Show>
                  <div className="flex justify-between items-center flex-1">
                    <div>
                      <p className="text-base font-medium text-content-700">
                        {group.applicant_name}
                      </p>
                      <p className="text-sm font-medium text-content-300">
                        {getApplicantRole(
                          group.applicant_role as ApplicantType,
                          group.is_primary_applicant
                        )}
                      </p>
                    </div>
                    <h5 className="text-base font-bold text-content-1000">
                      {toMoney((applicantTotal?.total ?? 0) / 100)}/mo
                    </h5>
                  </div>
                </div>

                <Show if={group.debts.length > 0}>
                  <div className="px-8 py-4 space-y-4">
                    {group.debts.map((debt) => (
                      <DebtLineItem
                        key={debt.id}
                        debt={debt}
                        checked={Boolean(includedDebts[debt.id])}
                        onToggle={() => toggleDebt(debt.id)}
                        isEditDenied={isEditDenied}
                      />
                    ))}
                  </div>
                </Show>
              </div>
            );
          })}
        </div>

        <footer className="px-6 py-4 border-t border-alpha-40 flex items-center justify-between shrink-0">
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.TEXT}
            className="!text-content-500"
            onClick={resetToDefaults}
            disabled={isEditDenied}
            tooltip={isEditDenied ? EDIT_DENIED_TOOLTIP : undefined}
          >
            Reset to defaults
          </Button>
          <div className="flex gap-2">
            <Button size={ButtonSize.SM} onClick={onClose}>
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              onClick={() => saveChanges()}
              loading={isPending}
              disabled={isEditDenied}
              tooltip={isEditDenied ? EDIT_DENIED_TOOLTIP : undefined}
            >
              Save changes
            </Button>
          </div>
        </footer>
      </Show>
    </Modal>
  );
};
